//#region 1️⃣ Angular Imports
import {NgModule} from '@angular/core';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {ReactiveFormsModule} from '@angular/forms';
//#endregion
//#region 2️⃣ Ionic & Angular Material
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatButtonModule} from '@angular/material/button';
import {MatRippleModule} from '@angular/material/core';
//#endregion
//#region 3️⃣ NgRx Store & Effects
import {Action, ActionReducer, MetaReducer, StoreModule} from '@ngrx/store';
import {EffectsModule} from '@ngrx/effects';
import {provideStoreDevtools} from '@ngrx/store-devtools';
import {LetDirective, PushPipe} from '@ngrx/component';
//#endregion
//#region 4️⃣ Environment Config
import {environment} from '../../../environments/environment';
//#endregion
//#region 5️⃣ Core Routing
import {CoreRoutingModule} from './core-routing.module';
//#endregion
//#region 6️⃣ Core Feature Store
import {coreReducer} from './store/core.reducer';
import {CoreEffects} from './store/core.effects';
//#endregion
//#region 7️⃣ Other Feature Stores
import {NgpReportEffects} from '../ngp-report/store/ngp-report.effects';
import {ngpReportReducer} from '../ngp-report/store/ngp-report.reducer';
import {NotificationEffects} from '../notifications/store/notification.effects';
import {notificationReducer} from '../notifications/store/notification.reducer';
import {ShelfTalkersEffects} from '../shelf-talkers/store/shelf-talkers.effects';
import {shelfTalkersReducer} from '../shelf-talkers/store/shelf-talkers.reducer';
import {SettingsReducer} from '../settings/store/settings.reducer';
import {SettingsEffects} from '../settings/store/settings.effects';
import {StockManagerEffects} from '../stock-manager/store/stock-manager.effects';
import {stockManagerReducer} from '../stock-manager/store/stock-manager.reducer';
import {StockHistoryEffects} from '../stock-history/store/stock-history.effects';
import {stockHistoryReducer} from '../stock-history/store/stock-history.reducer';
import {sharedFilterReducer} from './store-shared-filter/shared-filter.reducer';
import {SharedFilterEffects} from './store-shared-filter/shared-filter.effects';
import {sharedGridReducer} from '../../shared-modules/shared-grid/store/shared-grid.reducer';
import {SharedGridEffects} from '../../shared-modules/shared-grid/store/shared-grid.effects';
import {sharedReducer} from './store-shared/shared.reducer';
import {SharedEffects} from './store-shared/shared.effects';
import {userAccessReducer} from '../user-access/user-access-store/user-access.reducer';
import {UserAccessEffects} from '../user-access/user-access-store/user-access.effects';
import {IUserState, userReducer} from '../user-access/user-store/user.reducer';
import {UserEffects} from '../user-access/user-store/user.effects';
import {userStoresReducer} from '../user-access/user-stores-store/user-store.reducer';
import {UserStoresEffects} from '../user-access/user-stores-store/user-store.effects';
import {heartbeatReducer} from './store-heartbeat/heartbeat.reducer';
import {HeartbeatEffects} from './store-heartbeat/heartbeat.effects';
import {userDeviceReducer} from '../user-access/user-device-store/user-device.reducer';
import {UserDeviceEffects} from '../user-access/user-device-store/user-device.effects';
import {autoOrderingReducer} from '../auto-ordering/store/auto-ordering.reducer';
import {AutoOrderingEffects} from '../auto-ordering/store/auto-ordering.effects';
//#endregion
//#region 8️⃣ Shared Modules
import {SharedModule} from '../../shared-modules/shared-module/shared-module.module';
import {SharedIconsModule} from '../../shared-modules/shared-icons/shared-icons.module';
import {SharedComponentsModule} from '../../shared/shared-components/shared-components.module';
import {UiComponentsModule} from '../../shared/shared-module-ui/ui-components.module';
//#endregion
//#region 9️⃣ Feature Modules
import {NotificationsModule} from '../notifications/notifications.module';
import {UserAccessPageModule} from '../user-access/user-access.module';
//#endregion
//#region 🔟 Components
import {CORE_COMPONENTS} from './components';
import {
  GlobalModalContainerComponent,
} from '../global-modal-container/components/global-modal-container/global-modal-container.component';
import {AgGridAngular} from 'ag-grid-angular';
import {logConsoleAction, logConsoleState} from '../../shared/shared-module-ui/utils/logger/logger';
//#endregion

const isDevOrQA = environment.environment === 'Dev' || environment.environment === 'QA';
const userFlag = false;

export const debug = (reducer: ActionReducer<unknown>): ActionReducer<unknown> =>
  (state, action: Action) => {
    if (![
      '[Core Shared][Time Stamps] Get Data Time Stamps Success',
      '[Core Shared][Time Stamps] Get Data as Admin Time Stamps Success',
    ].includes(action.type)) {
      if ((state as { user: IUserState })?.user?.user?.logState?.logState === true) {
        logConsoleState('State Meta', state);
      }
      if ((state as { user: IUserState })?.user?.user?.logState?.logActions === true) {
        logConsoleAction(`${action.type}`, action);
      }
    }
    return reducer(state, action);
  };

export const metaReducers: MetaReducer[] = isDevOrQA ? [debug] : [];


//#endregion

@NgModule({
  declarations: [
    GlobalModalContainerComponent,
    ...CORE_COMPONENTS,
  ],
  imports: [
    //#region 1️⃣ Angular & Core
    CommonModule,
    CoreRoutingModule,
    ReactiveFormsModule,
    //#endregion

    //#region 2️⃣ Ionic & Angular Material
    MatTooltipModule,
    MatButtonModule,
    MatRippleModule,
    NgOptimizedImage,
    //#endregion

    //#region 8️⃣ Shared Modules
    SharedIconsModule,
    SharedModule,
    SharedComponentsModule,
    UiComponentsModule,
    //#endregion

    //#region 9️⃣ Feature Modules
    NotificationsModule,
    UserAccessPageModule,
    //#endregion

    //#region 3️⃣ NgRx Store
    StoreModule.forRoot(
      {
        ['core']: coreReducer,
        ['core-shared']: sharedReducer,
        ['heartbeat']: heartbeatReducer,
        ['ngp-report']: ngpReportReducer,
        ['notification']: notificationReducer,
        ['settings']: SettingsReducer,
        ['shared-filter']: sharedFilterReducer,
        ['shared-grid']: sharedGridReducer,
        ['shelf-talkers']: shelfTalkersReducer,
        ['stock-manager']: stockManagerReducer,
        ['auto-ordering']: autoOrderingReducer,
        ['stock-history']: stockHistoryReducer,
        ['user']: userReducer,
        ['user-access']: userAccessReducer,
        ['user-device']: userDeviceReducer,
        ['user-stores']: userStoresReducer,
      },
      {
        metaReducers,
        runtimeChecks: {
          strictActionImmutability: false,
          strictActionSerializability: false,
          strictStateSerializability: false,
        },
      },
    ),
    //#endregion

    //#region 3️⃣ NgRx Effects
    EffectsModule.forRoot([
      CoreEffects,
      HeartbeatEffects,
      NgpReportEffects,
      NotificationEffects,
      SharedEffects,
      SharedFilterEffects,
      SharedGridEffects,
      SettingsEffects,
      ShelfTalkersEffects,
      StockManagerEffects,
      AutoOrderingEffects,
      StockHistoryEffects,
      UserEffects,
      UserAccessEffects,
      UserDeviceEffects,
      UserStoresEffects,
    ]),
    //#endregion

    //#region 🔧 Utils & UI
    PushPipe,
    LetDirective,
    AgGridAngular,
    //#endregion
  ],
  exports: [
    GlobalModalContainerComponent,
    ...CORE_COMPONENTS,
  ],
  providers: [
    provideStoreDevtools(
      {
        maxAge: isDevOrQA ? 175 : 25,
        trace: isDevOrQA,
        logOnly: !isDevOrQA,
        actionsBlocklist: [
          '[Core Shared][Time Stamps] Get Data Time Stamps Success',
          '[Core Shared][Time Stamps] Get Data as Admin Time Stamps Success',
        ],
        traceLimit: 50,
      }),
  ],
})
export class CoreModule {
}
